import React, { useState, useEffect } from 'react';
import { MoreHorizontal, Edit3, Trash2, Archive } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { SessionService } from '../../../services/SessionService';
import styles from './ChatOptionsMenu.module.css';
import logger from '../../../logging/logger';

interface ChatOptionsMenuProps {
  sessionId: string;
  currentTitle: string;
  onRename: (sessionId: string, newTitle: string) => Promise<void>;
  onArchive: (sessionId: string) => Promise<void>;
  onRequestDelete: (sessionId: string, title: string) => void;
  activeMenuId: string | null;
  setActiveMenuId: (id: string | null) => void;
}

export const ChatOptionsMenu: React.FC<ChatOptionsMenuProps> = ({
  sessionId,
  currentTitle,
  onRename,
  onArchive,
  onRequestDelete,
  activeMenuId,
  setActiveMenuId
}) => {
  const isOpen = activeMenuId === sessionId;
  const [isRenaming, setIsRenaming] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [newTitle, setNewTitle] = useState(currentTitle);
  const [latestTitle, setLatestTitle] = useState(currentTitle);
  const { user, getToken } = useAuth();
  const sessionService = new SessionService();

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenuId(isOpen ? null : sessionId);
  };

  const handleRename = async () => {
    if (!newTitle.trim() || newTitle === latestTitle) {
      setIsRenaming(false);
      return;
    }
    try {
      await onRename(sessionId, newTitle.trim());
      setIsRenaming(false);
      setActiveMenuId(null);
    } catch (error) {
      setNewTitle(latestTitle);
    }
  };

  const handleArchive = async () => {
    try {
      await onArchive(sessionId);
      setActiveMenuId(null);
    } catch (error) {
      logger.error('Failed to archive chat:', error);
    }
  };

  const handleRequestDelete = () => {
    onRequestDelete(sessionId, currentTitle);
    setActiveMenuId(null);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest(`.${styles.menuContainer}`)) {
        setActiveMenuId(null);
        setIsRenaming(false);
        setIsArchiving(false);
        setNewTitle(latestTitle);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, currentTitle, setActiveMenuId, latestTitle]);

  if (isRenaming) {
    return (
      <div className={styles.renameContainer} onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRename();
            if (e.key === 'Escape') {
              setIsRenaming(false);
              setNewTitle(latestTitle);
            }
          }}
          onBlur={handleRename}
          className={styles.renameInput}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div className={`${styles.menuContainer} menuContainer ${isOpen ? styles.active : ''}`}>
      <button className={styles.menuButton} onClick={handleToggle}>
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          {isArchiving ? (
            <div className={styles.confirmDelete}>
              <p>Archive this chat?</p>
              <button onClick={handleArchive} className={styles.deleteConfirm}>
                Archive
              </button>
              <button onClick={() => setIsArchiving(false)} className={styles.deleteCancel}>
                Cancel
              </button>
            </div>
          ) : (
            <>
              <button
                className={styles.menuItem}
                onClick={async () => {
                  // Fetch latest title from server before showing rename dialog
                  try {
                    if (user?.email) {
                      const token = await getToken();
                      const session = await sessionService.getSession(sessionId, user.email, token || '');
                      setLatestTitle(session.title);
                      setNewTitle(session.title);
                    }
                  } catch (error) {
                    logger.error('Failed to fetch latest title:', error);
                    // Fallback to current title if fetch fails
                    setLatestTitle(currentTitle);
                    setNewTitle(currentTitle);
                  }
                  setIsRenaming(true);
                  setActiveMenuId(null);
                }}
              >
                <Edit3 className="h-4 w-4" />
                <span>Rename</span>
              </button>
              <button
                className={styles.menuItem}
                onClick={() => setIsArchiving(true)}
              >
                <Archive className="h-4 w-4" />
                <span>Archive</span>
              </button>
              <button
                className={`${styles.menuItem} ${styles.deleteItem}`}
                onClick={handleRequestDelete}
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};